import React, { useState } from "react";

import BoringAvatar from "boring-avatars";
import { twJoin } from "tailwind-merge";

import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { OrganizationUser } from "src/clients/taktile-api";
import { getSize, SizeName } from "src/design-system/utils/sizes";
import { tailwindSizeToPixels } from "src/utils/tailwindcss";
import { getUserName } from "src/utils/user";

export const FALLBACK_COLORS = [
  "#5946E5",
  "#6366F1",
  "#A7F3D0",
  "#22D3EE",
  "#493FC1",
];

// Extract from Sizes to make sure we only use the allowed sizes
export type AvatarSize = Extract<SizeName, "md" | "lg" | "3xl" | "6xl">;

type AvatarUser = Pick<
  OrganizationUser,
  "full_name" | "avatar_url" | "username"
>;
type AvatarProps =
  | {
      user: AvatarUser;
      size?: AvatarSize;
      isLoading?: false;
    }
  | {
      isLoading: true;
      size?: AvatarSize;
      user?: AvatarUser;
    };

/**
 * Figma: https://www.figma.com/design/BO9DWO8PKNGNSI3HhpSobA/Design-System-v2?node-id=5111-40963&node-type=frame&m=dev
 */
export const Avatar: React.FC<AvatarProps> = (props) => {
  const [isFailed, setIsFailed] = useState(false);
  const sizeInfo = getSize(props.size ?? "md");

  if (props.isLoading) {
    return (
      <SkeletonPlaceholder
        height={sizeInfo.height}
        rounded="rounded-full"
        width={sizeInfo.width}
        shrink0
      />
    );
  }

  const userName = getUserName(props.user);
  const initialLetter = (userName ?? "").substring(0, 1);

  if (props.user.avatar_url && !isFailed) {
    return (
      <div
        className={twJoin(
          "flex shrink-0 justify-center overflow-hidden rounded-full",
          sizeInfo.width,
          sizeInfo.height,
        )}
      >
        <img
          alt={userName}
          className="object-cover"
          src={props.user.avatar_url}
          onError={() => setIsFailed(true)}
        />
      </div>
    );
  }

  return (
    <div
      aria-label={userName}
      className={twJoin(
        "relative flex shrink-0 items-center justify-center overflow-hidden rounded-full",
        sizeInfo.width,
        sizeInfo.height,
      )}
      role="img"
    >
      <BoringAvatar
        colors={FALLBACK_COLORS}
        name={userName}
        size={tailwindSizeToPixels(sizeInfo.width)}
        variant="marble"
      />
      <span
        aria-hidden="true"
        className={twJoin(
          "absolute text-shadow-sm text-white uppercase",
          (!props.size || props.size === "md") && "font-inter-medium-12px",
          props.size === "lg" && "font-inter-medium-12px",
          props.size === "3xl" && "font-inter-semibold-16px",
          props.size === "6xl" && "scale-[3] font-inter-semibold-16px",
        )}
      >
        {initialLetter}
      </span>
    </div>
  );
};
